import React from 'react';
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Card, Empty, Spin } from 'antd';
import { useLanguage } from '@/contexts/LanguageContext';
import { DownloadOutlined, FileTextOutlined, FileImageOutlined } from '@ant-design/icons';
import { exportToCSV, exportElementAsImage } from '@/utils/exportUtils';
import { Button, Dropdown } from 'antd';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text
            x={x}
            y={y}
            fill="#374151"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            style={{ fontSize: '11px', fontWeight: '500', transition: 'all 0.3s ease' }}
        >
            {`${name}: ${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

const PieChartComponent = ({
    data,
    dataKey,
    nameKey,
    title,
    loading = false,
    height = 400,
    allowExport = true,
    onClick = null
}) => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();

    if (loading) {
        return (
            <Card title={title}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
                    <Spin size="large" />
                </div>
            </Card>
        );
    }

    const chartId = `pie-chart-${title?.replace(/\s+/g, '-').toLowerCase()}`;

    const items = [
        {
            key: 'csv',
            label: t('common.exportCSV') || 'CSV',
            icon: <FileTextOutlined />,
            onClick: () => exportToCSV(data, title || 'analytics_data')
        },
        {
            key: 'image',
            label: t('common.exportImage') || 'Image',
            icon: <FileImageOutlined />,
            onClick: () => exportElementAsImage(chartId, title || 'chart')
        }
    ];

    if (!data || data.length === 0) {
        return (
            <Card title={title}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
                    <Empty description={t('common.noData')} />
                </div>
            </Card>
        );
    }

    return (
        <Card
            id={chartId}
            title={title}
            className="shadow-sm hover:shadow-md transition-shadow duration-300"
            extra={allowExport && (
                <Dropdown menu={{ items }} placement="bottomRight">
                    <Button
                        type="text"
                        icon={<DownloadOutlined />}
                    >
                        {t('common.export')}
                    </Button>
                </Dropdown>
            )}
        >
            <div style={{ width: '100%', height: height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="45%"
                            labelLine={true}
                            label={renderCustomizedLabel}
                            outerRadius="75%"
                            fill="#8884d8"
                            dataKey={dataKey}
                            nameKey={nameKey}
                            paddingAngle={2}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip 
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        />
                        <Legend
                            verticalAlign="bottom"
                            content={({ payload }) => (
                                <div 
                                    className="minimal-scrollbar"
                                    style={{ 
                                        display: 'flex', 
                                        flexWrap: 'nowrap', 
                                        overflowX: 'auto', 
                                        justifyContent: 'flex-start', 
                                        gap: '12px', 
                                        marginTop: '20px', 
                                        paddingBottom: '10px',
                                        paddingLeft: '5px',
                                        paddingRight: '5px'
                                    }}
                                >
                                    {payload.map((entry, index) => (
                                        <div
                                            key={`legend-${index}`}
                                            className="group cursor-pointer transition-all hover:scale-105"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                whiteSpace: 'nowrap',
                                                backgroundColor: '#f9fafb',
                                                border: '1px solid #f3f4f6',
                                                padding: '6px 12px',
                                                borderRadius: '12px',
                                                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                                                flexShrink: 0
                                            }}
                                            onClick={() => onClick && onClick(entry.payload)}
                                        >
                                            <div
                                                style={{ 
                                                    width: '10px', 
                                                    height: '10px', 
                                                    borderRadius: '50%', 
                                                    backgroundColor: entry.color,
                                                    flexShrink: 0 
                                                }}
                                            />
                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                                                {entry.value}
                                            </span>
                                            <span style={{ 
                                                fontSize: '10px', 
                                                color: '#9ca3af', 
                                                fontWeight: 'bold', 
                                                backgroundColor: '#fff', 
                                                padding: '2px 6px', 
                                                borderRadius: '6px', 
                                                border: '1px solid #f3f4f6',
                                                marginLeft: '4px'
                                            }}>
                                                {entry.payload[dataKey]}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

export default PieChartComponent;
